import React, { createContext, useContext, useLayoutEffect } from 'react'
import { Outlet } from 'react-router-dom'
import { curatorStudentsApiSlice } from '../../features/curator/curatorStudents'
import { ratingApiSlice } from '../../features/rating/ratingSlice'
import { sectionApiSlice } from '../../features/section/sectionSlice'
import { examAnalysisApiSlice } from '../../features/curator/examAnalysisSlice'
import { videonotesApiSlice } from '../../features/section/videonotesSlice'
import { videoLessonApiSlice } from '../../features/section/videoLessonSlice'
import { homeworkApiSlice } from '../../features/section/homeworkSlice'
import { courseSubjectApiSlice } from '../../features/course/courseSubjectSlice'
import { courseTopicApiSlice } from '../../features/course/courseTopicSlice'
import { testApiSlice } from '../../features/section/testSlice'
import { useAppDispatch } from '../../app/hooks'
import { usePersistedState } from '../../hooks/usePersistedState'
import { scopeSubject } from '../../app/scopeSubject'

// Кабинет пән басшы - те же страницы ментора, но только чтение и все менторы его предметов.
// Страницы ментора спрашивают здесь, в каком режиме открыты.
interface HeadScope {
    readOnly: boolean
    basePath: string
    // сервис басшысы: все предметы, фильтр «Пән»
    director?: boolean
}

const HeadScopeContext = createContext<HeadScope>({ readOnly: false, basePath: '/curator' })

export const useHeadScope = () => useContext(HeadScopeContext)

export const HeadLayout = () => (
    <HeadScopeContext.Provider value={{ readOnly: true, basePath: '/head' }}>
        <Outlet />
    </HeadScopeContext.Provider>
)

interface DirectorSubject {
    subjectId: number | null
    setSubjectId: (id: number | null) => void
}

const DirectorSubjectContext = createContext<DirectorSubject>({ subjectId: null, setSubjectId: () => {} })

export const useDirectorSubject = () => useContext(DirectorSubjectContext)

// данные этих страниц зависят от subjectId, который добавляет base query
const SCOPED_APIS = [
    curatorStudentsApiSlice, ratingApiSlice, sectionApiSlice, examAnalysisApiSlice, videonotesApiSlice,
    videoLessonApiSlice, homeworkApiSlice, courseSubjectApiSlice, courseTopicApiSlice, testApiSlice,
]

// Сервис басшысы: страницы басшы по всем предметам. Выбранный «Пән» уходит в каждый запрос
// (scopeSubject); при смене кэш сбрасывается и страница монтируется заново.
export const DirectorLayout = () => {
    const dispatch = useAppDispatch()
    const [subjectId, setStored] = usePersistedState<number | null>('director.subjectId', null)

    useLayoutEffect(() => {
        scopeSubject.id = subjectId
        return () => {
            scopeSubject.id = null
        }
    }, [subjectId])

    const setSubjectId = (id: number | null) => {
        scopeSubject.id = id
        SCOPED_APIS.forEach((api) => dispatch(api.util.resetApiState() as any))
        setStored(id)
    }

    return (
        <HeadScopeContext.Provider value={{ readOnly: true, basePath: '/director', director: true }}>
            <DirectorSubjectContext.Provider value={{ subjectId, setSubjectId }}>
                <Outlet key={subjectId ?? 0} />
            </DirectorSubjectContext.Provider>
        </HeadScopeContext.Provider>
    )
}

// У басшы в выборе группы сразу виден ментор: «География KZ-10 · Айым Еділхан»
export const useGroupLabel = () => {
    const { readOnly } = useHeadScope()
    const { data: mentors = [] } = curatorStudentsApiSlice.useFetchHeadMentorsQuery(undefined, { skip: !readOnly })

    const mentorsOf = (groupId: number) =>
        mentors
            .filter((m) => m.groupId === groupId)
            .map((m) => `${m.firstName} ${m.lastName}`.trim())
            .join(', ')

    return (group: { id: number; title: string }) => {
        const names = readOnly ? mentorsOf(group.id) : ''
        return names ? `${group.title} · ${names}` : group.title
    }
}
